/* eslint-disable jira/restricted/fedramp-isolated-cloud */
import {
	getEdition,
	PREMIUM_EDITION,
	UNLICENSED,
	toEdition,
} from '@jira/platform__application-editions/src/edition.tsx';
import {
	JIRA_PRODUCT_DISCOVERY,
	JIRA_SOFTWARE,
} from '@jira/platform__application-editions/src/application.tsx';
import { graphql, useFragment } from 'react-relay';

import { isToday } from 'date-fns';

import { fetchSiteTraitsResource } from '@jira/router-resources__recommendations/src/fetchSiteTraitsResource.tsx';
import { fetchSiteUserTraitsResource } from '@jira/router-resources__recommendations/src/fetchSiteUserTraitsResource.tsx';
import type { PersonalizationApiTraitData } from '@jira/router-resources__recommendations/src/PersonalizationApiTraitData.types.tsx';
import { isFedRamp } from '@atlaskit/atlassian-context/is-fedramp';
import { fg } from '@atlassian/jira-feature-gating';
import { isFeatureEnabled } from '@atlassiansox/frontend-feature-encoding';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { getFirstActivationDate } from '@atlassian/jira-common-util-get-tenant-context';
import { useResource } from '@atlassian/react-resource-router';
import { useRoadmapUpsellAdControls } from './useRoadmapUpsellAdControls.tsx';
import type { useShowRoadmapsUpsell$key } from './__generated__/useShowRoadmapsUpsell.graphql.ts';

function traitListIndicatesOptOut(
	traits: PersonalizationApiTraitData[] | null | undefined,
): boolean {
	if (!traits) {
		return false;
	}
	for (const item of traits) {
		if (
			!item ||
			(item.name !== 'optout_all_messages' && item.name !== 'optout_all_messages_user')
		) {
			continue; // eslint-disable-line no-continue
		}
		return item.value === 'true' || item.value === true;
	}
	return false;
}

export const useShowRoadmapsUpsell = (queryRef: useShowRoadmapsUpsell$key): boolean => {
	/* eslint-disable @atlassian/relay/use-fragment-naming */
	/* eslint-disable @atlassian/relay/no-wrapping-hooks */
	/* eslint-disable @atlassian/relay/graphql-naming */
	const data = useFragment<useShowRoadmapsUpsell$key>(
		graphql`
			fragment useShowRoadmapsUpsell on Query {
				jira {
					stringUserProperty(
						cloudId: $cloudId
						accountId: $ownerAri
						propertyKey: "jpd-roadmaps-upsell.dismissed"
					) {
						stringValue
					}
				}
			}
		`,

		queryRef,
	);
	const dismissedUserPropertyValue = data.jira?.stringUserProperty?.stringValue === 'true';
	const appEditions = useAppEditions();
	const isFedRampInstance = fg('aem_roadmap_in_nav')
		? !isFeatureEnabled('jira', 'jira-roadmap-in-nav')
		: isFedRamp();
	const isSiteAdmin = useIsSiteAdmin();
	const firstActivationDateMs = getFirstActivationDate();
	const jiraSoftwareEdition = getEdition(JIRA_SOFTWARE, appEditions);
	const jpdEdition = getEdition(JIRA_PRODUCT_DISCOVERY, appEditions);
	const { isDismissed: isDismissedFromAdControls } = useRoadmapUpsellAdControls();
	const wasSiteActivatedToday = firstActivationDateMs
		? isToday(new Date(firstActivationDateMs))
		: false;

	const { data: userTraitsData } = useResource(fetchSiteUserTraitsResource);
	const { data: siteTraitsData } = useResource(fetchSiteTraitsResource);
	const hasOptedOut =
		traitListIndicatesOptOut(userTraitsData) || traitListIndicatesOptOut(siteTraitsData);

	const isDismissed = dismissedUserPropertyValue ? true : isDismissedFromAdControls;

	return (
		!hasOptedOut &&
		!wasSiteActivatedToday &&
		!isFedRampInstance &&
		!isDismissed &&
		isSiteAdmin &&
		jiraSoftwareEdition === toEdition(PREMIUM_EDITION) &&
		jpdEdition === toEdition(UNLICENSED) &&
		expValEquals('jpd-roadmaps-in-jira-navigation', 'cohort', 'variation', 'non-enrolled')
	);
};
